import { IStairConfig, IStepGeometry } from '@stair-craft/common/util';

import { WINDER_ANGLE_DEG, WINDER_COUNT, WINDER_INNER_OFFSET } from '../l-shape-calculate-layout/calculate-layout';
import { centerToPosition } from '../shared-center-to-position/center-to-position';
import { rayWallIntersection } from '../shared-ray-wall-intersection/ray-wall-intersection';

export function generateWinderSteps(
    flight1Count: number,
    riserHeight: number,
    turnStartZ: number,
    config: IStairConfig,
    startIndex: number
): IStepGeometry[] {
    const steps: IStepGeometry[] = [];
    const sw: number = config.stairWidth;
    const isRight: boolean = config.turnDirection === 'right';
    const angleRad: number = (WINDER_ANGLE_DEG * Math.PI) / 180;

    // Pivot at inner corner of the turn
    const pivotX: number = isRight ? sw : 0;
    const pivotZ: number = turnStartZ;

    const arcSign: number = isRight ? -1 : 1;

    // Rectangular walls that the outer edge must follow
    const outerWalls: readonly { readonly axis: 'x' | 'z'; readonly value: number }[] = [
        { axis: 'x', value: isRight ? 0 : sw },
        { axis: 'z', value: turnStartZ + sw },
    ];

    // Wall corner where two outer walls meet — 90° angle point
    const wallCorner: { x: number; z: number } = { x: isRight ? 0 : sw, z: turnStartZ + sw };
    const cornerDx: number = wallCorner.x - pivotX;
    const cornerDz: number = wallCorner.z - pivotZ;
    const cornerAngle: number = Math.atan2(cornerDz, arcSign * cornerDx);

    for (let w: number = 0; w < WINDER_COUNT; w++) {
        const startAngle: number = w * angleRad;
        const endAngle: number = (w + 1) * angleRad;
        const midAngle: number = (w + 0.5) * angleRad;
        const winderY: number = (flight1Count + w + 1) * riserHeight;

        // Outer corners: ray from pivot intersects rectangular walls
        const outerBack: { x: number; z: number } = rayWallIntersection(
            pivotX,
            pivotZ,
            arcSign * Math.cos(startAngle),
            Math.sin(startAngle),
            outerWalls
        );
        const outerFront: { x: number; z: number } = rayWallIntersection(
            pivotX,
            pivotZ,
            arcSign * Math.cos(endAngle),
            Math.sin(endAngle),
            outerWalls
        );

        // Insert wall corner point if this step crosses the 90° corner
        const outerMidPoints: { x: number; z: number }[] =
            cornerAngle > startAngle + 1e-6 && cornerAngle < endAngle - 1e-6 ? [wallCorner] : [];

        // Inner corners: offset by WINDER_INNER_OFFSET from pivot so the narrow edge has non-zero width.
        // The "narrow edge" is then the chord between innerBack and innerFront (both at radius=offset).
        const innerBack: { x: number; z: number } = {
            x: pivotX + WINDER_INNER_OFFSET * arcSign * Math.cos(startAngle),
            z: pivotZ + WINDER_INNER_OFFSET * Math.sin(startAngle),
        };
        const innerFront: { x: number; z: number } = {
            x: pivotX + WINDER_INNER_OFFSET * arcSign * Math.cos(endAngle),
            z: pivotZ + WINDER_INNER_OFFSET * Math.sin(endAngle),
        };
        const winderCorners: NonNullable<IStepGeometry['winderCorners']> = {
            innerBack,
            innerFront,
            outerBack,
            outerFront,
            ...(outerMidPoints.length > 0 ? { outerMidPoints } : {}),
        };

        const outerDepth: number = Math.round(Math.sqrt((outerFront.x - outerBack.x) ** 2 + (outerFront.z - outerBack.z) ** 2));
        const innerDepth: number = Math.round(Math.sqrt((innerFront.x - innerBack.x) ** 2 + (innerFront.z - innerBack.z) ** 2));
        const treadW: number = sw;

        // Centroid of the four corners for position reference
        const cx: number = (pivotX + pivotX + outerBack.x + outerFront.x) / 4;
        const cz: number = (pivotZ + pivotZ + outerBack.z + outerFront.z) / 4;
        const pos: { x: number; z: number } = centerToPosition(cx, cz, treadW, outerDepth, 0);
        const rotationDeg: number = isRight ? -(midAngle * 180) / Math.PI : (midAngle * 180) / Math.PI;

        steps.push({
            index: startIndex + w,
            position: { x: pos.x, y: winderY, z: pos.z },
            treadWidth: treadW,
            treadDepth: outerDepth,
            treadThickness: config.treadThickness,
            // A winder overhangs the step below like a flight tread does.
            noseLength: config.noseLength,
            rotation: rotationDeg,
            isWinder: true,
            isLanding: false,
            riserHeight,
            innerDepth,
            outerDepth,
            winderCorners,
        });
    }

    return steps;
}
